import type {
  LiveSessionRequest,
  PlaybackSessionRequest,
  VgpsLiveSession,
  VgpsLivekitWebRuntime,
  VgpsPlaybackSession,
} from 'livekit_web';

type AnySession = VgpsLiveSession | VgpsPlaybackSession;

interface RegistryEntry {
  session: AnySession;
  refCount: number;
}

const registry = new Map<string, RegistryEntry>();

/**
 * In-flight teardowns, keyed by camera group (device + base channel).
 *
 * An SD/HD switch changes the channel (`*02` <-> `*01`), so it releases one
 * registry key and leases a different one. React runs the effect cleanup before
 * the next effect body, but `release()` is async and the hook does not await it
 * — so without this the old session's `session_stop` would race the new
 * session's `session_open`, and the device would be asked to push both renditions
 * at once. Recording the close here lets the next lease for the SAME camera wait
 * for it first.
 */
const pendingTeardown = new Map<string, Promise<void>>();

/** Camera identity, ignoring the SD/HD rendition suffix. */
function groupKeyOf(deviceSerial: string, channelId: string): string {
  return `${deviceSerial}:${String(channelId).replace(/0[12]$/, '')}`;
}

async function leaseSession<T extends AnySession>(
  key: string,
  groupKey: string,
  factory: () => Promise<T>,
): Promise<{ session: T; release: () => Promise<void> }> {
  // Wait out any close still in flight for this camera so the device is never
  // told to open the new rendition before the old one has been stopped.
  const pending = pendingTeardown.get(groupKey);
  if (pending) await pending.catch(() => undefined);

  let entry = registry.get(key);

  if (!entry) {
    const session = await factory();
    entry = { session, refCount: 0 };
    registry.set(key, entry);
  }

  entry.refCount += 1;

  return {
    session: entry.session as T,
    release: async (): Promise<void> => {
      const e = registry.get(key);
      if (!e) return;
      e.refCount -= 1;
      if (e.refCount > 0) return;
      registry.delete(key);

      // NOTE: everything up to and including the `pendingTeardown.set` below
      // must stay synchronous. The hook calls `release()` without awaiting it,
      // so the entry has to be registered before the next effect body runs its
      // `leaseSession` — otherwise that lease sees an empty map and races.
      const previous = pendingTeardown.get(groupKey) ?? Promise.resolve();
      const closing = previous
        .catch(() => undefined)
        .then(() => e.session.close())
        .finally(() => {
          if (pendingTeardown.get(groupKey) === closing) {
            pendingTeardown.delete(groupKey);
          }
        });
      pendingTeardown.set(groupKey, closing);
      await closing;
    },
  };
}

export async function leaseLiveSession(
  runtime: VgpsLivekitWebRuntime,
  req: { deviceSerial: string; channelId: string; qualityHint?: 'uhd' | 'hd' | 'sd' | 'auto' },
): Promise<{ session: VgpsLiveSession; release: () => Promise<void> }> {
  const key = `live:${req.deviceSerial}:${req.channelId}:${req.qualityHint ?? 'auto'}`;
  const request: LiveSessionRequest = {
    deviceSerial: req.deviceSerial,
    channelId: req.channelId,
    playOptions: { qualityHint: req.qualityHint },
  };
  return leaseSession(
    key,
    groupKeyOf(req.deviceSerial, req.channelId),
    () => runtime.openLiveSession(request),
  );
}

export async function leasePlaybackSession(
  runtime: VgpsLivekitWebRuntime,
  req: {
    deviceSerial: string;
    channelId: string;
    startTsMs: number;
    endTsMs: number;
    qualityHint?: 'uhd' | 'hd' | 'sd' | 'auto';
  },
): Promise<{ session: VgpsPlaybackSession; release: () => Promise<void> }> {
  const key = `playback:${req.deviceSerial}:${req.channelId}:${req.startTsMs}:${req.endTsMs}:${req.qualityHint ?? 'auto'}`;
  const request: PlaybackSessionRequest = {
    deviceSerial: req.deviceSerial,
    channelId: req.channelId,
    playOptions: { qualityHint: req.qualityHint },
    timeRange: { startTsMs: req.startTsMs, endTsMs: req.endTsMs },
  };
  return leaseSession(
    key,
    groupKeyOf(req.deviceSerial, req.channelId),
    () => runtime.openPlaybackSession(request),
  );
}

/** Clear all registry entries — call after runtime.dispose(). */
export function clearRegistry(): void {
  registry.clear();
  pendingTeardown.clear();
}
